
export const HOOKS_DEEP_DIVE_CONTENT = `
# React Hooks Deep Dive

## Advanced Hook Patterns
Learn to use hooks effectively and create custom hooks for complex state management and side effects.

## useEffect Mastery
\`\`\`jsx
// Effect with cleanup
useEffect(() => {
  const subscription = subscribe();
  return () => subscription.unsubscribe();
}, []);

// Effect with dependency optimization
useEffect(() => {
  if (shouldFetch) {
    fetchData();
  }
}, [shouldFetch, fetchData]);
\`\`\`

## useCallback and useMemo Optimization
\`\`\`jsx
const memoizedCallback = useCallback(() => {
  doSomething(a, b);
}, [a, b]);

const memoizedValue = useMemo(() => {
  return computeExpensiveValue(a, b);
}, [a, b]);
\`\`\`

## Custom Hook Patterns
\`\`\`jsx
function useLocalStorage(key, initialValue) {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      return initialValue;
    }
  });

  const setValue = (value) => {
    try {
      setStoredValue(value);
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  return [storedValue, setValue];
}
\`\`\`

## useReducer for Complex State
\`\`\`jsx
function useShoppingCart() {
  const [state, dispatch] = useReducer(cartReducer, { items: [] });
  
  const addItem = (item) => dispatch({ type: 'ADD_ITEM', item });
  const removeItem = (id) => dispatch({ type: 'REMOVE_ITEM', id });
  
  return { ...state, addItem, removeItem };
}
\`\`\`
`;
